
import Vue from 'vue';
import { TranslateResult } from 'vue-i18n';
import {
  Icon,
  Button,
  Loading,
  FloatingActionButton,
  DrawerNotes,
  AnnotationInputCard,
  ToggleSwitch,
  Toast,
} from '@descomplica/vue-components';
import {
  ArticleModel,
  ClassroomModel,
  CourseModel,
  ListModel,
  ListItemModel,
  QuestionModel,
  ReplyModel,
  UserModel,
  VideoModel,
  AssertionModel,
} from '@descomplica/dex-models';
import BackButton from '../components/BackButton.vue';
import StaticDialog from '../components/StaticDialog.vue';
import HeaderLessonMobile from '../components/HeaderLessonMobile.vue';
import Article from '../components/Article.vue';
import LessonMenu from '../components/LessonMenu.vue';
import Questions from '../components/Questions.vue';
import Trivia from './Trivia.vue';
import Practice from './Practice.vue';
import KalturaPlayer from '../components/KalturaPlayer.vue';
import { positionAscending } from '@/helpers/array';
import ListItemMenuModel from '../models/ListItemMenuModel';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { BaseTrackType } from '@/helpers/analytics/types';
import { ANALYTICS_LABELS } from '@/helpers/listItems';
import { getCourseCategory, DEFAULT_CATEGORY } from '@/helpers/category';
import i18n from '../i18n';
import { NoteModel, NotePlatform, SetNotePayloadModel } from '@/models/student-notes/note.model';
import { secondsToTimestamp, startOf } from '@/helpers/date';
import debounce from 'debounce';
import { UserSettingsModel } from '@/models/alumni-profile/user-settings.model';
import { NOTE_INTERACTION_TYPE_ENUM } from '@/helpers/note-interaction-type';
import { UserInteractionModel } from '@/models/alumni-profile/user-interaction.model';
import NoteOnboardingModal from '@/components/modals/NoteOnboardingModal.vue';
import { SharedNotebook } from '@/models/student-notes/shared-notebook.model';
import ShareModal from '../components/modals/ShareModal.vue';
import { AlumniProfileModel } from '@/models/alumni-profile/alumni-profile.model';
import MicrocertificateRequestsService from '@/helpers/postgradMicrocertificate/request';
import { shouldNotificateForNewMicrocertificate } from '@/helpers/postgradMicrocertificate/notificationCheck';
import { getDexToken } from '@/helpers/get-tokens';
import { PostgradAnalytics } from '@/helpers/analytics/postgradAnalytics';
import { WeekModel } from '@/models/studyPlanning/WeekModel';
import { UndergradAnalytics } from '@/helpers/analytics/undergradAnalytics';
import { UndergradGradesService, UserListStatus } from '@/services/UndergradGrades';
import SentryClient from '@/helpers/sentry';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import dompurify from 'dompurify';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';

interface Positions {
  horizontal: Number;
  vertical: Number;
}

interface PositionOptions {
  corner: String;
  desktop: Positions;
  mobile: Positions;
}

interface NoteToast {
  isVisible: boolean;
  success?: boolean;
  error?: boolean;
  description?: string;
}

interface Data {
  isLoading: Boolean;
  showDialogCloseBtn: Boolean;
  currentItem: ListItemModel | null;
  showNextContentQuestionButton: Boolean;
  isBulletListDisplayed: boolean;
  isNotesDrawerOpened: boolean;
  closeRecentlyNotesDrawer: boolean;
  fabAnnotationPosition: PositionOptions;
  badgeAnnotationPosition: PositionOptions;
  shouldShowNoteContent: boolean;
  shouldShowShareModal: boolean;
  currentNoteText: string;
  currentSelectedNote: NoteModel | null;
  currentNoteSeconds: number;
  timeouts: Array<number>;
  editingNoteId?: number;
  shouldAutoPauseVideo?: boolean;
  noteToast: NoteToast;
  isShareModalActive: boolean;
  shouldShowDraftMessage: boolean;
  newAnnotationKey: number;
  editNoteBackup?: string;
  loadingNoteId?: number;
  notesDrawerTabs: Array<string>;
  progressesDispatched: Array<number>;
  debounceNoteInput: (() => void) | null;
  currentListStatus?: UserListStatus;
}

interface IVideoPlayer extends Vue {
  getCurrentTime: () => number;
  pause: () => void;
  videoInstance: any;
}

export default Vue.extend({
  components: {
    KalturaPlayer,
    Icon,
    Button,
    FloatingActionButton,
    DrawerNotes,
    Article,
    Questions,
    Trivia,
    LessonMenu,
    BackButton,
    StaticDialog,
    Loading,
    HeaderLessonMobile,
    Practice,
    AnnotationInputCard,
    ToggleSwitch,
    Toast,
    NoteOnboardingModal,
    ShareModal,
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.currentItem = null;
    this.pullList();

    if (this.showDueDateWarning) {
      this.currentListStatus = undefined;
      this.setCurrentListStatus();
    }
  },
  props: {
    returnToPostgradStudyPlanning: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      currentItem: null,
      showDialogCloseBtn: false,
      isLoading: true,
      showNextContentQuestionButton: false,
      isBulletListDisplayed: false,
      isNotesDrawerOpened: false,
      closeRecentlyNotesDrawer: false,
      fabAnnotationPosition: {
        corner: 'top-right',
        desktop: {
          horizontal: 80,
          vertical: 78,
        },
        mobile: {
          horizontal: 20,
          vertical: 80,
        },
      },
      badgeAnnotationPosition: {
        corner: 'top-right',
        desktop: {
          horizontal: 80,
          vertical: 108,
        },
        mobile: {
          horizontal: 25,
          vertical: 118,
        },
      },
      shouldShowNoteContent: true,
      shouldShowShareModal: false,
      currentNoteText: '',
      currentSelectedNote: null,
      currentNoteSeconds: 0,
      timeouts: [],
      editingNoteId: undefined,
      shouldAutoPauseVideo: undefined,
      noteToast: {
        isVisible: false,
      },
      isShareModalActive: false,
      shouldShowDraftMessage: false,
      newAnnotationKey: 0,
      editNoteBackup: undefined,
      loadingNoteId: undefined,
      notesDrawerTabs: [],
      progressesDispatched: [],
      debounceNoteInput: null,
      currentListStatus: {},
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    currentCourse(): CourseModel & { progress: number; period: number } {
      return this.$store.getters.currentCourse;
    },
    isPostgraduateCourse(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
    currentAlumniProfile(): AlumniProfileModel {
      return this.$store.getters.currentAlumniProfile;
    },
    userCanViewPostgradStudyPlanning(): boolean {
      return this.$store.getters.canViewPostgradStudyPlanning;
    },
    studyPlanning(): StudyPlanningModel {
      return this.$store.getters.studyPlanning(this.currentCourse.id);
    },
    userHasActivePostgradStudyPlanning(): boolean {
      return Boolean(this.$store.getters.studyPlanning(this.currentCourse.id));
    },
    totalAnticipatedStudyPlanningWeeks(): number {
      const currentWeek: WeekModel = this.$store.getters.studyPlanningWeek;

      if (!currentWeek) return 0;

      const anticipatedWeeks = this.$store.getters.studyPlanningAnticipatedWeeks(
        this.studyPlanning.id,
      ) as WeekModel[];

      if (!anticipatedWeeks.length) return 0;

      const highestAnticipatedWeekPosition = Math.max(...anticipatedWeeks.map((w) => w.position));

      return highestAnticipatedWeekPosition - currentWeek.position;
    },
    postgradStudyPlanningTrackingProps(): Record<string, boolean | number> {
      return {
        postgrad_study_planning_feature_enabled: this.userCanViewPostgradStudyPlanning,
        has_postgrad_study_planning: this.userHasActivePostgradStudyPlanning,
        current_total_anticipated_study_planning_weeks: this.totalAnticipatedStudyPlanningWeeks,
      };
    },
    currentList(): ListModel {
      return this.$store.getters.listBySlug(this.$route.params.listSlug);
    },
    currentClassroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    currentClassroomRoutePath(): string {
      const currentRoutePath = window.location.href;
      return currentRoutePath.replace(/\/aula\/\S*/, '');
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
    listSlug(): string {
      return this.$route.params.listSlug;
    },
    teacher(): UserModel | null {
      return this.currentClassroom && this.currentClassroom.teachers.length
        ? this.currentClassroom.teachers[0]
        : null;
    },
    formatedItems(): Array<ListItemMenuModel> {
      const items: ListItemMenuModel[] = [];
      const { listItems } = this;

      for (let i = 0; i < listItems.length; i += 1) {
        const currentItem: ListItemModel = listItems[i];
        const previousItem: ListItemMenuModel = items[items.length - 1];

        if (previousItem && currentItem.isObjective() && previousItem.behavior === 'question') {
          previousItem.addListItem(currentItem);
        } else {
          items.push(new ListItemMenuModel(currentItem));
        }
      }

      return items;
    },
    nextList(): ListModel | undefined {
      const classroomLists = this.currentClassroom?.getLists() || [];
      const currentIndex = classroomLists.findIndex(
        (list: ListModel) => list.id === this.currentList.id,
      );
      const offset = currentIndex + 1 < classroomLists.length ? currentIndex + 1 : 0;
      const nextUnfinishedListIndex = classroomLists
        .slice(offset)
        .findIndex((list: ListModel) => !list.isCompleted());

      if (nextUnfinishedListIndex === -1) {
        const firstUnfinishedListIndex = classroomLists.findIndex(
          (list: ListModel) => !list.isCompleted(),
        );
        return classroomLists[firstUnfinishedListIndex];
      }

      return classroomLists[nextUnfinishedListIndex + offset];
    },
    listItems(): Array<ListItemModel> {
      if (this.currentList && this.currentList.listItems) {
        return this.currentList.listItems.slice(0).sort(positionAscending);
      }
      return [];
    },
    isCurrentArticle(): Boolean | null {
      return this.currentItem && this.currentItem.isArticle();
    },
    isCurrentQuestion(): Boolean | null {
      return this.currentItem && this.currentItem.isQuestion();
    },
    isCurrentVideo(): Boolean | null {
      return this.currentItem && this.currentItem.isVideo();
    },
    isCurrentTrivia(): Boolean | null {
      return this.currentItem && this.currentItem.isDiscursive();
    },
    isCurrentObjectiveExercise(): Boolean | null {
      return this.currentItem && this.currentItem.isObjective();
    },
    isCurrentPractice(): Boolean | null {
      return this.currentItem && this.currentItem.isPractice();
    },
    objectiveQuestions(): Array<QuestionModel | ArticleModel | VideoModel | undefined> {
      return this.listItems.reduce(
        (acc: Array<QuestionModel | ArticleModel | VideoModel | undefined>, listItem) => {
          if (listItem.isObjective()) acc.push(listItem.content);
          return acc;
        },
        [],
      );
    },
    userReplies(): Array<ReplyModel> {
      return this.$store.getters.repliesByListUser(this.currentList.id, this.user.id);
    },
    nextListItem(): ListItemModel | null {
      if (this.currentItem) {
        const { currentItem } = this;
        const index = this.formatedItems.findIndex((item) => item.id === currentItem.id);

        if (index < this.formatedItems.length - 1) {
          const item = this.formatedItems[index + 1];
          return item && item.listItemList ? item.listItemList[0] : null;
        }
      }

      return null;
    },
    nextBehaviorItem(): ListItemModel | null {
      if (this.currentItem) {
        let { currentItem } = this;
        const currentBehavior = this.currentItem.behavior;
        let index = this.formatedItems.findIndex(
          (item) => currentItem && currentItem.id === item.id,
        );

        while (currentItem && currentItem.behavior === currentBehavior) {
          index += 1;
          const item = this.formatedItems[index];
          // @ts-expect-error
          currentItem = item && item.listItemList ? item.listItemList[0] : null;
        }

        if (index < this.formatedItems.length) return currentItem;
      }

      return null;
    },
    videos(): Array<ListItemModel> {
      return this.listItems.filter((listItem) => listItem.isVideo());
    },
    incompletedVideos(): Array<ListItemModel> {
      return this.videos.filter((v) => !v.isCompleted());
    },
    isAllVideosCompleted(): boolean {
      return this.incompletedVideos.length === 0;
    },
    defaultTrackingProps(): BaseTrackType {
      return {
        course: this.currentCourse.name,
        discipline: this.currentClassroom.name,
        category: getCourseCategory(this.currentCourse),
        content_type: ANALYTICS_LABELS.question,
      };
    },
    shouldShowNotebooksPage(): boolean | null {
      return this.currentItem && this.currentItem.isVideo();
    },
    videoNotes(): NoteModel[] {
      return this.currentItem &&
        this.currentItem.isVideo() &&
        this.currentItem.video &&
        this.currentItem.video.id
        ? this.$store.getters.getVideoNotesByVideoId(this.currentItem.video.id)
        : [];
    },
    lastDraftNote(): NoteModel | undefined {
      return this.videoNotes.find((note) => note.draft != null && note.latestText === null);
    },
    isLoadingNotes(): boolean {
      return this.$store.getters.isLoadingNotes;
    },
    isLoadingNewNoteSave(): boolean {
      return this.$store.getters.isLoadingNewNoteSave && !!this.currentNoteText;
    },
    isConfirmationBoxOpened(): boolean {
      return this.$store.getters.isConfirmationBoxOpened;
    },
    isDeletingAnnotation(): boolean {
      return this.$store.getters.isDeletingAnnotation;
    },
    userSettings(): UserSettingsModel {
      return this.$store.getters.currentUserSettings;
    },
    videoName(): string {
      return this.currentItem && this.currentItem.isVideo() ? this.currentItem.video.name : '';
    },
    userInteraction(): UserInteractionModel {
      return this.$store.getters.currentUserInteraction;
    },
    openNoteOnboardingModal(): boolean | null {
      return (
        this.isNotesDrawerOpened &&
        !this.userInteraction?.completedNoteOnboardingDate &&
        this.shouldShowNotebooksPage
      );
    },
    currentLessonUrl(): string {
      return `/${this.currentCourse.descomplicaCategorySlug || DEFAULT_CATEGORY}/${
        this.currentCourse.slug
      }/turma/${this.currentClassroom.slug}/aula/${this.currentList.slug}`;
    },
    sharedNotebook(): SharedNotebook {
      return this.$store.getters.getSharedNotebook;
    },
    shareableUrl(): string {
      if (!this.sharedNotebook) return '';

      return `${window.location.host}/caderno-compartilhado/${this.sharedNotebook?.generatedLink}`;
    },
    isGeneratingSharedNotebookLink(): boolean {
      return this.$store.getters.isGeneratingSharedNotebookLink;
    },
    handleGeneratedLinkUrl(): TranslateResult | string {
      return this.isGeneratingSharedNotebookLink ? i18n.t('generatingLink') : this.shareableUrl;
    },
    classVideoNotes(): NoteModel[] {
      return this.shouldDisplayPublicVideoNotes &&
        this.currentItem &&
        this.currentItem.isVideo() &&
        this.currentItem.video &&
        this.currentItem.video.id
        ? this.$store.getters.getClassNotesByVideoId(this.currentItem.video.id)
        : [];
    },
    shouldDisplayPublicVideoNotes(): boolean {
      return this.$store.getters.shouldDisplayPublicVideoNotes;
    },
    isVideoSubtitlesActive(): boolean {
      const language = localStorage.getItem('kaltura-player-js_textLanguage');
      return !!language && language !== 'off';
    },
    isUndergraduateCourse(): boolean {
      return this.$store.getters.isUndergraduateCourse;
    },
    showDueDateWarning(): boolean {
      return this.isUndergraduateCourse && this.$store.getters.showDueDateWarning;
    },
    exerciseInUrl(): ListItemModel | undefined {
      const selectedQuestionId = this.$route.query.questionId;
      if (Number(selectedQuestionId) > 0) {
        return this.listItems.find((item) => {
          return item.questionId === Number(selectedQuestionId);
        });
      }
      return undefined;
    },
    questionComments(): UnifiedCommentModel[] | undefined {
      if (this.currentItem?.question) {
        const questionId = this.currentItem.question.id;
        return this.$store.getters
          .commentsByQuestionId(questionId)
          .concat()
          .sort(positionAscending);
      }

      return undefined;
    },
    sanitizedComments(): UnifiedCommentModel[] | undefined {
      if (this.isCurrentTrivia || this.isCurrentPractice) {
        const comments = this.questionComments || [];
        comments.forEach((comment) => {
          comment.text = dompurify.sanitize(comment.text);
        });
        return comments;
      }
      return undefined;
    },
  },
  watch: {
    shouldAutoPauseVideo(value, oldValue) {
      if (oldValue !== undefined && this.userSettings?.shouldAutoPauseVideo !== value)
        this.$store.dispatch('EDIT_USER_SETTINGS', value);
    },
    async currentItem() {
      await this.pullVideoNotes();
      this.$store.commit('SET_SHARED_NOTEBOOK', null);
    },
    videoNotes() {
      if (!this.sharedNotebook) this.setSharedNotebook();
    },
    currentNoteText() {
      this.shouldShowDraftMessage =
        this.lastDraftNote?.draft?.markdownText === this.currentNoteText;
    },
  },
  updated() {
    this.updateContent();
    this.handleDialogCloseBtn();
  },
  async created() {
    await this.pullList();

    if (this.showDueDateWarning) this.setCurrentListStatus();

    await this.pullVideoNotes();
    await this.setSharedNotebook();
  },
  mounted() {
    this.shouldAutoPauseVideo = this.userSettings?.shouldAutoPauseVideo;
    this.debounceNoteInput = debounce(
      () =>
        async function save(this: any) {
          await this.addNoteHandler({
            isDraft: true,
          });
        }.call(this),
      3000,
    );
  },
  beforeDestroy() {
    const { clearTimeout } = window;

    this.timeouts.map((timeout) => clearTimeout(timeout));
  },
  methods: {
    leaveLessonHandler(): void {
      if (this.returnToPostgradStudyPlanning) {
        this.$router.push({
          name: 'postgradStudyPlanning',
          params: {
            category: this.currentCourse.descomplicaCategorySlug as string,
            currentCourse: this.currentCourse.slug as string,
          },
        });

        return;
      }

      this.$router.push({
        name: 'classroom',
        params: {
          classroomSlug: this.$route.params.classroomSlug,
        },
      });
    },
    toggleBulletList(): void {
      this.isBulletListDisplayed = !this.isBulletListDisplayed;
    },
    goToNextListItem() {
      if (!this.currentItem || !this.nextListItem) {
        if (this.nextList) {
          this.$router.push({
            name: 'lesson',
            params: {
              classroomSlug: this.$route.params.classroomSlug,
              listSlug: this.nextList.slug,
            },
          });
        }
        return;
      }
      this.changeContent(this.nextListItem);
    },
    goToNextBehaviorItem() {
      if (this.nextBehaviorItem) {
        this.changeContent(this.nextBehaviorItem);
      }
    },
    changeContent(listItemModel: ListItemModel): void {
      if (listItemModel) {
        this.currentItem = listItemModel;
        this.updateContent();
        this.trackingNewContent();
        window.scrollTo(0, 0);
      }
    },
    async addNewProgress(item: ListItemModel): Promise<void> {
      const hasAlreadyDispatchedProgress = this.progressesDispatched.includes(item.id);
      const oldClassroomProgress = this.currentClassroom.progress;

      if (!item.progress && !hasAlreadyDispatchedProgress && (item.isVideo() || item.isArticle())) {
        this.progressesDispatched.push(item.id);
        const isProgressCreated = await this.$store.dispatch('CREATE_PROGRESS', {
          listItemId: item.id,
          userId: this.user.id,
        });

        if (isProgressCreated) {
          this.trackingProgress();
          this.trackingContentConsumed(item);

          this.$store
            .dispatch('PULL_CLASSROOMS_PROGRESS', {
              classroomIds: [this.currentClassroom.id],
            })
            .then(() => {
              const updatedClassroomProgress = this.currentClassroom.progress;

              if (this.isPostgraduateCourse && this.user.email) {
                this.handlePostgradClassroomProgress(
                  oldClassroomProgress ?? 0,
                  updatedClassroomProgress ?? 0,
                );
              }
            });
        } else {
          this.progressesDispatched = this.progressesDispatched.filter((id) => id !== item.id);
        }
      }
    },
    addNewProgressAndGoToNextListItem(): void {
      if (this.currentItem) {
        this.addNewProgress(this.currentItem);
      }

      this.goToNextListItem();
    },
    addNewProgressAndGoToNextBehaviorItem(): void {
      if (this.currentItem) {
        this.addNewProgress(this.currentItem);
      }

      this.goToNextBehaviorItem();
    },
    async pullList() {
      const { descomplicaCategorySlug } = this.currentCourse;
      this.isLoading = true;

      await this.$store.dispatch('PULL_LIST_ITEMS', {
        listSlug: this.listSlug,
        userId: this.user.id,
      });

      if (!this.currentList) {
        this.redirectToListNotFound();
        return;
      }

      const hasLists = this.currentClassroom && this.currentClassroom.getLists().length > 0;
      if (!hasLists) {
        await this.$store.dispatch('PULL_CLASSROOM_INFO', {
          userId: this.user.id,
          classroomSlug: this.$route.params.classroomSlug,
          courseCategory: descomplicaCategorySlug,
        });
      }
    },
    async setCurrentListStatus() {
      try {
        this.currentListStatus = await new UndergradGradesService().getListStatus(
          this.currentClassroom.id,
          this.currentList.id,
        );
      } catch (error) {
        SentryClient.captureError(error);
      }
    },
    setDraftAsCurrentNote(): void {
      this.shouldShowDraftMessage = !!this.lastDraftNote;
      this.currentNoteText = this.lastDraftNote?.draft?.markdownText || '';
      this.currentNoteSeconds = this.lastDraftNote?.videoTimeStamp || 0;
    },
    async pullVideoNotes() {
      if (this.currentItem?.video) {
        this.$store.commit('SET_LOADING_NOTES', true);

        await this.$store.dispatch('GET_USER_VIDEO_NOTES', {
          dexVideoId: this.currentItem.video.id,
        });

        if (this.shouldDisplayPublicVideoNotes) {
          await this.$store.dispatch('GET_CLASS_VIDEO_NOTES', {
            dexVideoId: this.currentItem.video.id,
          });

          this.handleDrawerTabTitles();
        }
        this.$store.commit('SET_LOADING_NOTES', false);
      }

      this.setDraftAsCurrentNote();

      this.shouldShowNoteContent = !!this.userInteraction?.readNoteIntroductionDate;
    },
    handleDrawerTabTitles() {
      const noteCount: number = this.videoNotes.filter((note) => !note.draft).length;
      const notesTitle: string = `${i18n.t('notesDrawerTabs.userNotes')} (${noteCount})`;
      const classNotesTitle: string = `${i18n.t('notesDrawerTabs.classNotes')} (${
        this.classVideoNotes.length
      })`;
      this.notesDrawerTabs = [notesTitle, classNotesTitle];
    },
    updateContent() {
      if (!this.currentItem) {
        const { firstIncompleteListItem } = this.currentList;
        const [listItem] = this.listItems;
        this.currentItem = this.exerciseInUrl || firstIncompleteListItem || listItem;
      } else if (!this.currentItem.isObjective()) {
        this.showNextContentQuestionButton = false;
      }
      this.isLoading = false;
    },
    selectListItem(listItemMenuModel: ListItemMenuModel) {
      if (this.currentItem && this.currentItem.id !== listItemMenuModel.id) {
        const foundListItem = this.listItems.find(
          (listItem: ListItemModel) => listItem.id === listItemMenuModel.id,
        );
        if (foundListItem) {
          this.changeContent(foundListItem);
        }
      }
    },
    addProgressToCurrentItemAndSelectListItem(listItemMenuModel: ListItemMenuModel) {
      if (this.currentItem) {
        this.addNewProgress(this.currentItem);
      }
      this.selectListItem(listItemMenuModel);

      if (this.isBulletListDisplayed) this.toggleBulletList();
    },
    addNewReply(data: { question: QuestionModel; assertionId: number }): void {
      const { question, assertionId } = data;

      const foundAssertion: AssertionModel | undefined = question.assertions.find(
        (assertion: AssertionModel) => assertion.id === assertionId,
      );

      if (foundAssertion && foundAssertion.id) {
        const answer = foundAssertion.position;
        const oldClassroomProgress = this.currentClassroom.progress;

        this.$store
          .dispatch('CREATE_REPLY', {
            listId: this.currentList.id,
            questionId: question.id,
            userId: this.user.id,
            assertionId,
            answer,
          })
          .then(async () => {
            await this.$store.dispatch('PULL_CLASSROOMS_PROGRESS', {
              classroomIds: [this.currentClassroom.id],
            });

            const updatedClassroomProgress = this.currentClassroom.progress;

            if (this.isPostgraduateCourse && this.user.email) {
              this.handlePostgradClassroomProgress(
                oldClassroomProgress ?? 0,
                updatedClassroomProgress ?? 0,
              );
            }
          });

        Analytics.trackEvent(EVENT_LABELS.contentConsumed, {
          ...this.defaultTrackingProps,
          lesson_slug: this.currentList.slug,
          lesson_name: this.currentList.name,
          content_id: question.id,
          content_status: foundAssertion.correct ? 'right' : 'wrong',
          ...(this.isPostgraduateCourse && this.postgradStudyPlanningTrackingProps),
          ...this.getTrialStatusByDescomplicaCourseId,
          ...this.earlyGraduationTrackingProps,
        });

        if (this.isPostgraduateCourse) this.updatePostgradEngagementProperties('exercise');
        if (this.isUndergraduateCourse) this.updateUndergradEngagementProperties('exercise');
      }
    },
    handleDialogCloseBtn() {
      if (this.currentList && this.currentList.isCompleted()) {
        setTimeout(() => {
          this.showDialogCloseBtn = true;
        }, 1800);
      }
    },
    trackingProgress() {
      Analytics.trackEvent(EVENT_LABELS.courseProgress, {
        ...this.defaultTrackingProps,
        lesson_slug: this.currentList.slug,
        lesson_name: this.currentList.name,
        course_slug: this.currentCourse.slug,
        course_progress: this.currentCourse.progress,
      });

      Analytics.trackEvent(EVENT_LABELS.disciplineProgress, {
        ...this.defaultTrackingProps,
        lesson_slug: this.currentList.slug,
        lesson_name: this.currentList.name,
        discipline_slug: this.currentClassroom.slug,
        discipline_progress: this.currentClassroom.progress,
      });
    },
    trackingContentConsumed(item: ListItemModel) {
      if (item && item.behavior) {
        const extraProps: { [key: string]: string | number | boolean } = {};

        if (item.isArticle()) extraProps.content_status = 'read';
        if (item.isVideo()) {
          extraProps.content_progress = 100;
          extraProps.subtitle = this.isVideoSubtitlesActive;
        }

        Analytics.trackEvent(EVENT_LABELS.contentConsumed, {
          ...this.defaultTrackingProps,
          ...extraProps,
          content_id: item.id,
          lesson_slug: this.currentList.slug,
          lesson_name: this.currentList.name,
          content_type: ANALYTICS_LABELS[item.behavior],
          ...(this.isPostgraduateCourse && this.postgradStudyPlanningTrackingProps),
          ...this.getTrialStatusByDescomplicaCourseId,
          ...this.earlyGraduationTrackingProps,
        });

        if (this.isPostgraduateCourse)
          this.updatePostgradEngagementProperties(item.isArticle() ? 'article' : 'video');

        if (this.isUndergraduateCourse)
          this.updateUndergradEngagementProperties(item.isArticle() ? 'article' : 'video');
      }
    },
    trackingNewContent() {
      if (this.currentItem && this.currentItem.behavior) {
        Analytics.trackEvent(EVENT_LABELS.contentOpened, {
          ...this.defaultTrackingProps,
          content_type: ANALYTICS_LABELS[this.currentItem.behavior],
          lesson_name: this.currentList.name,
          lesson_slug: this.currentList.slug,
        });
      }
    },
    listItemBehavior(listItem: ListItemModel | null): TranslateResult {
      if (listItem) {
        return i18n.tc(listItem.behavior as string);
      }

      return i18n.tc('nextList');
    },
    setShowNextContentQuestionButton(newValue: boolean) {
      this.showNextContentQuestionButton = newValue;
    },
    redirectToListNotFound() {
      this.$router.push({
        name: 'NotFound',
      });
    },
    toggleNotesDrawer(active: boolean): void {
      this.isNotesDrawerOpened = active;

      if (this.isNotesDrawerOpened) {
        this.noteInteractionAnalytics(NOTE_INTERACTION_TYPE_ENUM.LIST_ANNOTATIONS);
      }

      if (this.isNotesDrawerOpened && this.userSettings?.shouldAutoPauseVideo) {
        (this.$refs.videoPlayer as IVideoPlayer).pause();
      }

      if (!this.isNotesDrawerOpened) {
        this.closeRecentlyNotesDrawer = true;
        const { setTimeout } = window;

        this.timeouts.push(
          setTimeout(() => {
            this.closeRecentlyNotesDrawer = false;
          }, 2000),
        );
      }
    },
    toggleShowContent(): void {
      const now = new Date();
      this.$store.dispatch('SET_USER_INTERACTION', { readNoteIntroductionDate: now });
      this.shouldShowNoteContent = true;
    },
    toggleShowShareModal(): void {
      this.shouldShowShareModal = false;
    },
    handleEmptyInputClick(): void {
      if (!this.currentNoteText)
        this.currentNoteSeconds = (this.$refs.videoPlayer as IVideoPlayer).getCurrentTime();
    },
    async addNoteHandler({ isDraft }: { isDraft: boolean }): Promise<void> {
      if (!this.currentItem || !this.currentNoteText.trim()) return;

      if (this.isLoadingNewNoteSave) {
        this.$store.commit('SET_LOADING_NEW_NOTE_SAVE', false);
        return;
      }

      this.$store.commit('SET_LOADING_NEW_NOTE_SAVE', true);

      const note: SetNotePayloadModel = {
        id: this.lastDraftNote?.id,
        dexVideoId: this.currentItem?.video.id,
        dexLessonId: this.currentList.id,
        dexDisciplineId: this.currentClassroom.id,
        originUrl: this.currentLessonUrl,
        videoTimeStamp: this.currentNoteSeconds,
        platform: NotePlatform.dex,
        text: {
          id: this.lastDraftNote?.draft?.id,
          markdownText: this.currentNoteText,
          isDraft,
        },
      };

      await this.$store.dispatch('SET_USER_VIDEO_NOTE', {
        ...note,
        saveNoteToClass: this.shouldDisplayPublicVideoNotes,
      });

      if (this.shouldDisplayPublicVideoNotes) this.handleDrawerTabTitles();

      this.$store.commit('SET_LOADING_NEW_NOTE_SAVE', false);

      if (!isDraft) {
        this.currentNoteText = '';
        this.newAnnotationKey += 1;
        this.noteInteractionAnalytics(NOTE_INTERACTION_TYPE_ENUM.SAVE_ANNOTATION);
      } else {
        this.noteInteractionAnalytics(NOTE_INTERACTION_TYPE_ENUM.UPDATE_DRAFT);
      }
      this.shouldShowDraftMessage = !!this.lastDraftNote;
    },
    getTimestampFromNote(seconds: number): string {
      return secondsToTimestamp(seconds);
    },
    onDeleteAnnotation(note: NoteModel): void {
      this.$store.commit('SET_CONFIRMATION_BOX', true);
      this.currentSelectedNote = note;
      this.scrollToCurrentSelectedNote();
    },
    onEditAnnotation(note: NoteModel): void {
      this.editingNoteId = note.id;
      this.editNoteBackup = note.latestText.markdownText;
    },
    async onCopyAnnotation(note: NoteModel): Promise<void> {
      await this.$store.dispatch('COPY_VIDEO_NOTE', {
        id: note.id,
        originUrl: this.currentLessonUrl,
      });
      this.handleDrawerTabTitles();
    },
    cancelDelete(): void {
      this.$store.commit('SET_CONFIRMATION_BOX', false);
      this.currentSelectedNote = null;
    },
    async deleteAnnotation(): Promise<void> {
      this.$store.commit('SET_DELETING_ANNOTATION', true);

      this.noteInteractionAnalytics(NOTE_INTERACTION_TYPE_ENUM.DELETE_ANNOTATION);

      const deleteResult = await this.$store.dispatch('DELETE_USER_VIDEO_NOTE', {
        id: this.currentSelectedNote?.id,
      });

      this.noteToast = {
        success: deleteResult.success || false,
        error: deleteResult.error || false,
        isVisible: true,
        description: deleteResult.description,
      };

      this.$store.commit('SET_DELETING_ANNOTATION', false);
      this.$store.commit('SET_CONFIRMATION_BOX', false);

      this.currentSelectedNote = null;

      const { setTimeout } = window;

      this.timeouts.push(
        setTimeout(() => {
          this.noteToast.isVisible = false;
        }, 2000),
      );

      if (this.shouldDisplayPublicVideoNotes) this.handleDrawerTabTitles();
    },
    isHighlighted(noteId: Number): boolean {
      return this.currentSelectedNote?.id === noteId;
    },
    scrollToCurrentSelectedNote() {
      const annotationInputCard = this.$refs[
        `annotation-${this.currentSelectedNote?.id}`
      ] as Array<any>;

      if (annotationInputCard) {
        annotationInputCard[0].$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    shouldEditNote(noteId: number): boolean {
      return this.editingNoteId ? noteId === this.editingNoteId : false;
    },
    async saveNoteHandler(note: NoteModel): Promise<void> {
      if (!note?.latestText?.markdownText) return;

      this.loadingNoteId = note.id;

      const newText = note.latestText.markdownText;

      await this.$store.dispatch('SET_USER_VIDEO_NOTE', {
        ...note,
        dexLessonId: this.currentList.id,
        dexDisciplineId: this.currentClassroom.id,
        originUrl: this.currentLessonUrl,
        text: {
          markdownText: newText,
          isDraft: false,
        },
        saveNoteToClass: this.shouldDisplayPublicVideoNotes,
      });

      this.noteInteractionAnalytics(NOTE_INTERACTION_TYPE_ENUM.EDIT_ANNOTATION);

      this.editingNoteId = undefined;
      this.loadingNoteId = undefined;
    },
    async cancelEditNoteHandler(note: NoteModel): Promise<void> {
      if (!note?.latestText?.markdownText) return;
      if (!this.editNoteBackup) return;

      note.latestText.markdownText = this.editNoteBackup;

      this.editNoteBackup = undefined;
      this.editingNoteId = undefined;
    },
    onFocusAnnotationInputCardComponent(value: string) {
      if (!value) {
        this.noteInteractionAnalytics(NOTE_INTERACTION_TYPE_ENUM.NEW_ANNOTATION);
      }
    },
    noteInteractionAnalytics(interactionType: NOTE_INTERACTION_TYPE_ENUM) {
      Analytics.trackEvent(EVENT_LABELS.noteInteracted, {
        ...this.defaultTrackingProps,
        lesson_slug: this.currentList.slug,
        lesson_name: this.currentList.name,
        content_name: this.videoName || 'no-name-defined',
        content_type: ANALYTICS_LABELS.video,
        interaction_type: interactionType,
      });
    },
    toggleShareModal(active: boolean): void {
      this.isShareModalActive = active;
    },
    onCloseNoteOnboarding() {
      const currentDate = new Date();
      this.$store.dispatch('SET_USER_INTERACTION', { completedNoteOnboardingDate: currentDate });
    },
    async setSharedNotebook(): Promise<void> {
      if (!this.videoNotes || this.videoNotes?.filter((note) => !note.draft).length === 0) return;

      const firstAnnotation: NoteModel = this.videoNotes[0];

      await this.$store.dispatch('SET_SHARED_NOTEBOOK', firstAnnotation.dexDisciplineId);

      this.shouldShowShareModal = !!this.videoNotes && this.videoNotes?.length > 0;
    },
    handleShareableUrlCopy(): void {
      if (!this.videoNotes || this.videoNotes?.length === 0 || !this.shareableUrl) return;

      navigator.clipboard.writeText(this.shareableUrl);

      const firstAnnotation: NoteModel = this.videoNotes[0];

      Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
        category: getCourseCategory(this.currentCourse),
        course: this.currentCourse.name,
        discipline: firstAnnotation.dexDiscipline?.name,
        interaction_type: 'Copy Link Clicked',
      });
    },
    async handleSocialShareClick(): Promise<void> {
      await navigator.share({
        title: i18n.t('descomplicaUniversity').toString(),
        text: i18n
          .t('shareNotebookContentTitle', {
            discipline: this.currentClassroom.name,
            url: this.shareableUrl,
          })
          .toString(),
      });

      Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
        discipline: this.currentClassroom.name,
        interaction_type: 'Share Options Clicked',
      });
    },
    async shareDiscipline(): Promise<void> {
      Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
        category: getCourseCategory(this.currentCourse),
        course: this.currentCourse.name,
        discipline: this.currentClassroom.name,
        interaction_type: 'Share Button Clicked',
      });
      this.isShareModalActive = true;
      await this.$store.dispatch('SET_SHARED_NOTEBOOK', this.currentClassroom.id);
    },
    handleCopiedLink(): void {
      Analytics.trackEvent(EVENT_LABELS.notebookInteracted, {
        category: getCourseCategory(this.currentCourse),
        course: this.currentCourse.name,
        discipline: this.currentClassroom.name,
        interaction_type: 'Copy Link Clicked',
      });
    },
    handleDeleteDraft(): void {
      if (!this.lastDraftNote) return;

      this.$store.dispatch('DELETE_USER_VIDEO_NOTE', {
        id: this.lastDraftNote.id,
      });
      this.currentNoteText = '';
    },
    isNoteCreatedByUser(note: NoteModel): boolean {
      return this.currentAlumniProfile.id === note.profileId;
    },
    isPostgradMicrocertificateEnabledForBoardOfLesson(): boolean {
      const boardsClassroomIds = this.currentClassroom.boardsClassroomsByClassroomId;

      if (boardsClassroomIds.length !== 1) {
        // in postgrad context, a classroom should always have only one board
        return false;
      }

      const boardClassroom = this.$store.getters.boardsClassroomById(boardsClassroomIds[0]);

      if (!boardClassroom) {
        return false;
      }

      return boardClassroom.board?.hasPostgradMicrocertificateEnabled ?? false;
    },
    handlePostgradClassroomProgress(
      oldClassroomProgress: number,
      updatedClassroomProgress: number,
    ): void {
      if (
        !this.isPostgraduateCourse ||
        !shouldNotificateForNewMicrocertificate(oldClassroomProgress, updatedClassroomProgress) ||
        !this.isPostgradMicrocertificateEnabledForBoardOfLesson()
      )
        return;

      MicrocertificateRequestsService.handleClassroomProgress(getDexToken()!, {
        email: this.user.email!,
        oldClassroomProgress,
        classroomName: this.currentClassroom.name,
        classroomId: this.currentClassroom.id,
        classroomUrl: this.currentClassroomRoutePath,
        tracking: {
          course: this.currentCourse,
          earlyGraduationTrackingProps: this.earlyGraduationTrackingProps,
        },
      });
    },
    async updatePostgradEngagementProperties(
      contentType: 'article' | 'exercise' | 'video',
    ): Promise<void> {
      const propertyNames = {
        article: 'last_article_read_date',
        exercise: 'last_exercise_answered_date',
        video: 'last_lesson_played_date',
      };

      PostgradAnalytics.updateHubSpotContactProperties({
        [propertyNames[contentType]]: startOf(new Date()).getTime(),
      });
    },

    async updateUndergradEngagementProperties(
      contentType: 'article' | 'exercise' | 'video',
    ): Promise<void> {
      const propertyNames = {
        article: 'last_article_read_date',
        exercise: 'last_exercise_answered_date',
        video: 'last_lesson_played_date',
      };

      UndergradAnalytics.updateHubSpotContactProperties({
        [propertyNames[contentType]]: startOf(new Date()).getTime(),
      });
    },
  },
});
